import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import { ProductVariantFragment } from "client/graphql/commerce-v2-api"
import { ProductTitle } from "client/packages/commerce/components/ui/textStyles"
import { scaleShopifyImageUrl } from "commerce/utils"
import { FormattedMessage } from "react-intl"
import { AccentButton } from "shared/components/buttons/AccentButton"
import { TextButton } from "shared/components/buttons/TextButton"
import { P } from "shared/styles/textStyles"
import styled from "styled-components"

import {
  getCartTotalItemCount,
  TargetCartItem,
  useTargetCartContext,
} from "./TargetCartContext"

const ClickAway = styled.div`
  position: absolute;
  margin-top: ${(props) => props.theme.commerce.headerHeight};
  width: 100%;
  height: ${(props) =>
    `calc(100% - ${props.theme.commerce.headerHeight})`};
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
`

const Panel = styled.div<{ $show: boolean }>`
  background-color: ${(props) => props.theme.colors.neutral};
  position: absolute;
  width: 100%;
  padding-top: ${(props) => props.theme.commerce.headerHeight};
  z-index: 2;
  transform: ${(props) =>
    props.$show ? "translateY(0%)" : "translateY(-100%)"};
  transition: transform 300ms ease-out;
`

const Message = styled(P)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 10px;
  font-family: "Target", Helvetica, sans-serif;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 10px;

  & button {
    margin-bottom: 10px;
  }
`

const ProductPreview = styled.div`
  display: flex;
  padding: 10px 20px;
`

export const ImageWrapper = styled.div`
  height: 60px;
  width: 100px;
  position: relative;
  margin-right: 10px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;

  svg {
    max-width: 100%;
    max-height: 100%;
  }
`

const Title = styled(ProductTitle)`
  font-weight: 600;
  font-size: 1rem;
  text-align: left;
  margin: 0px 0 7px;
  font-family: "Target", Helvetica, sans-serif !important;
`

const Subtitle = styled.p`
  text-align: left;
  margin: 5px 0;
  color: ${(props) => props.theme.text.default};
  font-family: "Target", Helvetica, sans-serif;
`

export type AddedToCartConfirmationProps = {
  cartItem?: TargetCartItem | null
  productVariant?: ProductVariantFragment | null
  handleShowCart?: () => void
  continueShopping?: () => void
  dismissModal?: () => void
}

const TargetAddedToCartConfirmation = ({
  cartItem,
  productVariant,
  continueShopping = () => {},
  handleShowCart = () => {},
  dismissModal = () => {},
}: AddedToCartConfirmationProps) => {
  const { contents } = useTargetCartContext()

  return (
    <>
      {productVariant && cartItem ? (
        <ClickAway onClick={dismissModal} />
      ) : null}
      <Panel $show={!!productVariant}>
        {productVariant && cartItem ? (
          <>
            <Message>
              {cartItem.quantity === 1 ? (
                <FormattedMessage
                  id="cart.confirmation"
                  defaultMessage="Item saved"
                />
              ) : (
                <FormattedMessage
                  id="cart.confirmationPlural"
                  defaultMessage="Items saved"
                />
              )}
            </Message>
            <ProductPreview>
              <ImageWrapper>
                {productVariant.featuredImage ? (
                  <img
                    src={scaleShopifyImageUrl(
                      productVariant.featuredImage.url,
                    )}
                    alt={
                      productVariant.featuredImage.altText ??
                      productVariant.title
                    }
                  />
                ) : (
                  <ImagePlaceholder />
                )}
              </ImageWrapper>
              <div>
                <Title $maxLines={2}>{productVariant.title}</Title>
                <Subtitle>
                  <FormattedMessage
                    id="confirmation.viewCart"
                    defaultMessage="Qty: {quantity}"
                    values={{ quantity: cartItem.quantity }}
                  />
                </Subtitle>
              </div>
            </ProductPreview>

            <ButtonContainer>
              <AccentButton onClick={handleShowCart}>
                <FormattedMessage
                  id="confirmation.viewCart"
                  defaultMessage="View saved items ( {quantity} )"
                  values={{ quantity: getCartTotalItemCount(contents) }}
                />
              </AccentButton>
              <TextButton onClick={continueShopping}>
                <FormattedMessage
                  id="confirmation.continueShopping"
                  defaultMessage="Continue Shopping"
                />
              </TextButton>
            </ButtonContainer>
          </>
        ) : null}
      </Panel>
    </>
  )
}

export default TargetAddedToCartConfirmation
